import type {Props} from './types';
import {Box} from '@jobseeker/lotus/foundations';
import {useIsCustomWidth, useWrappedTheme} from '@jobseeker/lotus/hooks';
import Loader from '@jobseeker/lotus/components/Loader';
import {Typography} from '@mui/material';
import {ChevronLeft, ChevronLeftOrange} from '../../assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {Button, ButtonV2} from '@jobseeker/lotus/elements';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {useSearchParams} from 'next/navigation';
import {useEffect} from 'react';
import {
  getUbaPageName,
  handleTracking,
  showErrorToast,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const RegistrationFooter = (props: Props) => {
  const {
    skipCTA,
    backCTA,
    submitCTA,
    onSubmit,
    submitDisabled,
    loading,
    steps,
    disableFooter,
    onSkip,
  } = props;

  const theme = useWrappedTheme();

  const searchParams = Object.fromEntries(useSearchParams().entries());
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const onBack = () => {
    const activeSteps = steps.filter(step => step.active);
    if (activeSteps?.length && activeSteps[0]?.backUrl) {
      openUrl(activeSteps[0].backUrl, '_self');
    }
  };

  const sendGAClickEvent = () => {
    if (
      submitCTA === 'Finish' &&
      (searchParams?.has == '1' || searchParams?.hir == '1')
    ) {
      handleTracking('regFinish', {
        event_category: 'Profile',
        Origin: page,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
      });
    }
  };

  const skipAction = async () => {
    const activeSteps = steps.filter(step => step.active);
    sendGAClickEvent();
    onSkip && (await onSkip());
    if (searchParams?.redirectto) {
      openUrl(searchParams?.redirectto, '_self');
    } else if (activeSteps?.length && activeSteps[0]?.forwardUrl) {
      openUrl(activeSteps[0].forwardUrl, '_self');
    }
  };

  const isSmall = useIsCustomWidth('md');

  const handleSwitchToApp = () => {
    TrackEventUBA('appDownloadLinkageClick', 'click', {
      platform: process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist',
      linkageName: 'Switch to App on Edit Profile',
      linkageType: 'downloadButton',
      typeClick: 'CTA',
      sessionUsername: `${sessionStorage.getItem('userId')}`,
      page: getUbaPageName(),
    });

    openUrl(
      isAppIimjobs
        ? 'https://iimjobs.onelink.me/TyLF/9x53dur4'
        : 'https://hirist.onelink.me/Z1Mi/fp5xkdf8',
      '_blank'
    );
  };

  const ButtonOnboard = isAppIimjobs ? ButtonV2 : Button;

  useEffect(() => {
    if (!skipCTA && isSmall) {
      TrackEventUBA('appDownloadLinkageView', 'view', {
        platform: process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist',
        linkageName: 'Switch to App on Edit Profile',
        linkageType: 'downloadButton',
        sessionUsername: `${sessionStorage.getItem('userId')}`,
        page: getUbaPageName(),
      });
    }
  }, [skipCTA, isSmall]);

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      width="100%"
      height={{xxs: '72px', md: '94px'}}
      boxShadow={'0px -6.52px 6px 0px #29292908'}
      sx={{
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        mx={{xxs: 0, md: 'auto'}}
        maxWidth={{md: '1110px', xl: '1185px'}}
        width={'100%'}
        display={'flex'}
        justifyContent={{xxs: 'flex-start', md: 'space-between'}}
        alignItems={'center'}
        py={{xxs: 2, md: 3.5}}
        paddingLeft={{xxs: 2, md: 6.5, lg: 2}}
        paddingRight={{xxs: 2, md: 2, lg: 1, xl: 5.5}}
      >
        <Box>
          {backCTA && (
            <Box
              display={{xxs: 'none', md: 'flex'}}
              justifyContent={'center'}
              alignItems={'center'}
              gap={0.625}
              onClick={onBack}
              sx={{
                cursor: 'pointer',
              }}
            >
              <Box
                width={1.5}
                height={1.5}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{
                  transform: 'rotate(180deg)',
                }}
              >
                <Image
                  src={isAppIimjobs ? ChevronLeft : ChevronLeftOrange}
                  alt=""
                  width={12}
                  height={12}
                />
              </Box>
              <Typography
                variant="subtitle1"
                color="primary.main"
                lineHeight={'24px'}
              >
                {backCTA}
              </Typography>
            </Box>
          )}
        </Box>
        {!skipCTA && isSmall && (
          <Box
            onClick={() => handleSwitchToApp()}
            sx={{
              display: {xs: 'flex', md: 'none'},
              position: 'fixed',
              maxWidth: '80%',
              cursor: 'pointer',
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box
                width={21}
                height={21}
                display="flex"
                alignItems="flex-end"
                justifyContent="center"
              >
                <Image
                  src={
                    isAppIimjobs
                      ? 'https://iimjobs-static-content.s3.ap-south-1.amazonaws.com/icon/iphone-app.svg'
                      : 'https://hirist-staticcontent.s3.ap-south-1.amazonaws.com/icon/iphone-orange-app.svg'
                  }
                  alt=""
                  width={21}
                  height={21}
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    lineHeight: '100%',
                    marginBottom: '-2px !important',
                  }}
                >
                  Switch to App
                </Typography>
                <Typography
                  variant="body3"
                  sx={{
                    color: ' #727272',
                  }}
                >
                  for a better experience
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <Box
          component="form"
          autoComplete="on"
          noValidate
          display={'flex'}
          alignItems={'center'}
          justifyContent={skipCTA ? 'space-between' : 'flex-end'}
          gap={{xxs: 0, md: 6.25}}
          width={{xxs: '100%', md: 'fit-content'}}
          onSubmit={e => {
            e.preventDefault();
            sendGAClickEvent();
            onSubmit && onSubmit(null, true);
          }}
        >
          {skipCTA && (
            <Typography
              variant="subtitle1"
              color="primary.main"
              lineHeight={'24px'}
              onClick={() => {
                if (disableFooter) {
                  try {
                    showErrorToast("Can't switch while recording");
                  } catch {}
                } else {
                  skipAction && skipAction();
                }
              }}
              sx={{
                ':hover': {
                  cursor: 'pointer',
                },
              }}
            >
              {skipCTA}
            </Typography>
          )}
          {submitCTA && (
            <ButtonOnboard
              type="submit"
              disabled={submitDisabled}
              sx={{
                minWidth: {xxs: '150px', md: '205px'},
                px: {xxs: 2, md: 6},
                py: 1,
                backgroundColor: isAppIimjobs
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main,
                fontSize: theme.typography.pxToRem(18),
                fontWeight: 500,
                lineHeight: '24px',
                color: isAppIimjobs ? 'default' : 'white',
                '&:hover': {
                  color: 'white',
                  backgroundColor: 'primary.main',
                },
              }}
              variant={isAppIimjobs ? 'contained' : 'outlined'}
              size="large"
            >
              {loading ? (
                <Loader
                  size={24}
                  sx={{
                    color: '#ffffff',
                  }}
                />
              ) : (
                submitCTA
              )}
            </ButtonOnboard>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RegistrationFooter;
